import { goRequest, goClient } from '../utils/httpClientTs';
import {
  PEOReportListResponse,
  ReportMetadata,
  ExecuteReportRequest,
  ExecuteReportResponse,
  EmailExportResponse,
  ReportAsyncEnqueueResponse,
  ReportExecutionStatusResponse,
} from '../types/peoReports';

export const getPEOReportList = async (): Promise<PEOReportListResponse> => {
  return goRequest<PEOReportListResponse>({
    url: '/api/reports',
    method: 'GET',
  });
};

export const getPEOReportMetadata = async (
  reportId: string,
  options?: { fund_type_id?: string }
): Promise<ReportMetadata> => {
  return goRequest<ReportMetadata>({
    url: `/api/reports/${reportId}`,
    method: 'GET',
    params:
      options?.fund_type_id != null && options.fund_type_id !== ''
        ? { fund_type_id: options.fund_type_id }
        : undefined,
  });
};

export const executePEOReport = async (
  reportId: string,
  params: ExecuteReportRequest
): Promise<ExecuteReportResponse> => {
  return goRequest<ExecuteReportResponse>({
    url: `/api/reports/${reportId}/execute`,
    method: 'POST',
    data: params,
  });
};

export const exportPEOReportEmails = async (
  reportId: string,
  params: Omit<ExecuteReportRequest, 'format'>
): Promise<EmailExportResponse> => {
  return goRequest<EmailExportResponse>({
    url: `/api/reports/${reportId}/execute`,
    method: 'POST',
    data: { ...params, format: 'emails' },
  });
};

/**
 * Downloads report as PDF (binary). Only supported for Active/Inactive Chapter Members.
 * Uses responseType: 'blob' so the response is not parsed as JSON.
 */
export const downloadPEOReportPDF = async (
  reportId: string,
  params: Omit<ExecuteReportRequest, 'format'>
): Promise<Blob> => {
  try {
    const response = await goClient.post<Blob>(
      `/api/reports/${reportId}/execute`,
      { ...params, format: 'pdf' },
      { responseType: 'blob' }
    );
    return response.data;
  } catch (err: any) {
    const data = err.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json.error || text);
      } catch {
        throw new Error(text || 'PDF download failed');
      }
    }
    throw err;
  }
};

export const enqueuePEOReportAsync = async (
  reportId: string,
  params: Omit<ExecuteReportRequest, 'format'>
): Promise<ReportAsyncEnqueueResponse> => {
  return goRequest<ReportAsyncEnqueueResponse>({
    url: `/api/reports/${reportId}/execute-async`,
    method: 'POST',
    data: params,
  });
};

export const getPEOReportExecutionStatus = async (
  taskLogId: string
): Promise<ReportExecutionStatusResponse> => {
  return goRequest<ReportExecutionStatusResponse>({
    url: `/api/reports/executions/${taskLogId}`,
    method: 'GET',
  });
};
